/* eslint-disable no-console -- log details while on devmode */
"use client";
import { cn, type RadioItem, useMediaQuery } from "@kyan/ui";
import { useEffect } from "react";

import {
  getPerpConstraints,
  getPerpSizeDisplayPrecision,
} from "#/config/orders";
import { SLIPPAGE_CONFIG } from "#/config/slippage";
import { tooltips } from "#/config/tooltips";
import { useSettings } from "#/hooks/useSettings";
import { useValidatePerpSize } from "#/hooks/useValidatePerpSize";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { useMarket } from "#/store/app/portfolio/market";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpMarketOrderType } from "#/store/perps/tradePanel/perpMarketOrderType";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { usePerpUnit } from "#/store/perps/tradePanel/perpUnit";
import { MarketOrder } from "#/types/orders";
import { calcPerpPriceWithSlippage } from "#/utils/calcPerpPriceWithSlippage";
import { formatCurrency } from "#/utils/valueFormatters";

import { PRICE_EXCESS_BOUNDS } from "./PerpMarketOrder.constants";

const marketSubOrderTypes: RadioItem<MarketOrder>[] = [
  {
    id: MarketOrder.IOC,
    label: "IOC",
    className: "px-4",
    tooltip: tooltips.IOC,
  },
  {
    id: MarketOrder.FOK,
    label: "FOK",
    className: "px-4",
    tooltip: tooltips.FOK,
  },
];

const percentages: RadioItem<string>[] = SLIPPAGE_CONFIG.perps.values.map(
  (value) => ({
    id: String(value),
    label: `${value}%`,
    className: (isActive) =>
      cn("w-13.5", {
        "bg-primary/10": isActive,
        "bg-buttonFill": !isActive,
      }),
  }),
);

export const usePerpMarketOrderController = () => {
  const { isSm } = useMediaQuery();
  const { market } = useMarket();
  const { perpSizeInputValue, setPerpSizeInputValue } = usePerpSize();
  const { perpUnit, perpUnits, isUnderlyingSelected, setPerpUnit } =
    usePerpUnit();
  const { perpIsBuy } = usePerpIsBuy();
  const { marketIndexPrice } = useMarketIndexPrice();
  const { perpLimitPrice, setPerpLimitPrice } = usePerpLimitPrice();
  const { perpMarketOrderType, setPerpMarketOrderType } =
    usePerpMarketOrderType();
  const { sizeError, sizeWarning } = useValidatePerpSize();
  const sizeDisplayPrecision = getPerpSizeDisplayPrecision(
    market.underlying,
    isUnderlyingSelected,
  );
  const { setSlippagePercentage, slippagePercentage, slippageFactor } =
    useSettings();
  const { isDevMode } = useIsDevMode();
  const handleUpdateSlippage = (value: string) => {
    setSlippagePercentage(Number(value));
  };

  const handleUpdatePerpSize = (size: string | number | undefined) => {
    setPerpSizeInputValue(String(size));
  };

  const handleToggleSubOrderType = (id: MarketOrder) => {
    setPerpMarketOrderType(id);
  };

  const isCustomSlippage =
    !SLIPPAGE_CONFIG.perps.values.includes(slippagePercentage);
  const slippageValue = String(slippagePercentage);
  const isIndexPriceLoaded = Boolean(marketIndexPrice);

  const priceDiff = perpIsBuy
    ? Number(perpLimitPrice) / Number(marketIndexPrice)
    : Number(marketIndexPrice) / Number(perpLimitPrice);
  const isSlippageExceeded =
    priceDiff >= 1 + (slippagePercentage * PRICE_EXCESS_BOUNDS.upper) / 100 ||
    priceDiff <= 1 + (slippagePercentage * PRICE_EXCESS_BOUNDS.lower) / 100;
  const priceIncrementDecimals =
    getPerpConstraints(market.underlying)?.priceDisplayPrecisionNotional ?? 0;

  useEffect(() => {
    if (!isIndexPriceLoaded || !isSlippageExceeded) return;
    const newLimitPriceWithSlippage = calcPerpPriceWithSlippage({
      isBuy: perpIsBuy,
      slippageFactor,
      indexPrice: marketIndexPrice,
      priceIncrementDecimals,
    });

    if (isDevMode) {
      console.log(
        "New Perp Limit Price: ",
        formatCurrency(Number(newLimitPriceWithSlippage), {
          precision: priceIncrementDecimals,
        }),
      );
    }
    setPerpLimitPrice(newLimitPriceWithSlippage);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Automatically updates when slippage is out of bounds without updating on each index price change.
  }, [
    isSlippageExceeded,
    market,
    isIndexPriceLoaded,
    slippagePercentage,
    perpIsBuy,
  ]);

  return {
    isSm,
    perpUnit,
    perpUnits,
    sizeError,
    percentages,
    sizeWarning,
    slippageValue,
    isCustomSlippage,
    perpSizeInputValue,
    marketSubOrderTypes,
    perpMarketOrderType,
    sizeDisplayPrecision,
    isUnderlyingSelected,
    setPerpUnit,
    handleUpdateSlippage,
    handleUpdatePerpSize,
    handleToggleSubOrderType,
  };
};
